/**
 * Timezone display helpers.
 *
 * Competition dates are configured against the account's address timezone. When
 * an account has no address (or no timezone on it) we fall back to the viewer's
 * own timezone rather than silently assuming UTC — UTC is wrong for nearly
 * everyone, whereas the viewer's own zone matches what they see on their clock.
 */

/** Returns the viewer's IANA timezone, or '' if the runtime cannot report one. */
export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

const isSupportedTimezone = (timezone: string): boolean => {
  try {
    // Throws RangeError for an unknown zone
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format()
    return true
  } catch {
    return false
  }
}

/**
 * The timezone registration dates should actually be interpreted in: the
 * account's when it is set and valid, otherwise the viewer's own.
 *
 * @param accountTimezone - IANA timezone from the account address
 * @returns a usable IANA timezone name
 */
export const resolveEffectiveTimezone = (
  accountTimezone?: string | null
): string => {
  if (accountTimezone && isSupportedTimezone(accountTimezone)) {
    return accountTimezone
  }

  return getBrowserTimezone()
}

/**
 * Short abbreviation for a timezone at a given instant — 'PDT', 'EST', 'GMT+9'.
 * DST-sensitive, so the instant matters: America/Vancouver is PDT in August and
 * PST in January.
 *
 * @param timezone - IANA timezone name
 * @param at - instant to evaluate the abbreviation at (defaults to now)
 * @returns the abbreviation, or '' when the zone is unusable
 */
export const getTimezoneAbbreviation = (
  timezone: string,
  at: Date = new Date()
): string => {
  if (!timezone) {
    return ''
  }

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(at)

    return parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

/**
 * Human-facing timezone label for registration date fields.
 *
 * Shows the abbreviation ('PDT') when the account has a configured timezone.
 * When falling back to the viewer's zone, the label says so — the admin should
 * be able to tell that the times are being interpreted in their own timezone
 * because the account has none, not because someone chose that.
 *
 * @param accountTimezone - IANA timezone from the account address
 * @param at - instant to evaluate the abbreviation at (defaults to now)
 */
export const formatTimezoneLabel = (
  accountTimezone?: string | null,
  at: Date = new Date()
): string => {
  const usingAccountTimezone = Boolean(
    accountTimezone && isSupportedTimezone(accountTimezone)
  )
  const effective = resolveEffectiveTimezone(accountTimezone)
  const abbreviation = getTimezoneAbbreviation(effective, at)

  if (usingAccountTimezone) {
    // Fall back to the IANA name if the zone has no short form at all
    return abbreviation || effective
  }

  const shown = abbreviation || effective
  return shown ? `${shown} — Local Time` : 'Local Time'
}
